import { api } from 'constants/constant';
import { getHeaders } from 'auth/auth';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export const documentAPI = createApi({
  reducerPath: 'Document',
  tagTypes: ['updateList', 'documentInsights'],
  baseQuery: fetchBaseQuery({ baseUrl: api, credentials: 'include' }),
  endpoints: (builder) => ({
    // Legacy aggregate endpoint used by older UIs
    getDocumentList: builder.query({
      query: ({ applicationId, borrowerId } = {}) => {
        // Call the aggregate endpoint so the frontend receives the grouped payload
        // Optionally scope to a borrower using borrower_id query param
        const qs = borrowerId ? `?borrower_id=${borrowerId}` : '';
        return {
          url: `documents/aggregate/loan/${applicationId}${qs}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      providesTags: ['updateList'],
    }),
    // B2C v2 endpoint using aggregate-style grouped payload under `documents`
    getB2cDocumentList: builder.query({
      query: ({ applicationId, borrowerId } = {}) => {
        const params = {};
        if (applicationId) params.applicationId = applicationId;
        if (borrowerId) params.borrowerId = borrowerId;
        return {
          url: `b2c/loan/documents`,
          method: 'GET',
          params,
          headers: getHeaders(),
        };
      },
      transformResponse: (response) => ({
        documents: response?.documents ?? [],
        total: Array.isArray(response?.documents) ? response.documents.length : 0,
        skip: 0,
        limit: Array.isArray(response?.documents) ? response.documents.length : 0,
      }),
      providesTags: ['updateList'],
    }),
    getUserProperty: builder.query({
      query: () => {
        return {
          // Use the documents/user_info endpoint provided by the core documents router
          url: `documents/user_info`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),
    deleteDocument: builder.mutation({
      query: ({ documentId }) => {
        const headers = getHeaders();
        delete headers.accept;
        headers.Accept = 'application/json';
        return {
          url: `documents/${documentId}`,
          method: 'DELETE',
          headers,
        };
      },
      invalidatesTags: ['updateList'],
    }),
    deleteB2cDocument: builder.mutation({
      query: ({ documentId }) => {
        const headers = getHeaders();
        delete headers.accept;
        headers.Accept = 'application/json';
        return {
          url: `b2c/loan/documents/${documentId}`,
          method: 'DELETE',
          headers,
        };
      },
      invalidatesTags: ['updateList'],
    }),
    uploadPredefinedDocs: builder.mutation({
      query: ({ data, additionalParams }) => {
        const formData = new FormData();
        formData.append('file', data);
        const {
          document_category_id,
          document_type_id,
          documentTitle,
          title,
          applicationId,
          loanApplicationId,
          userId,
          borrowerId,
          tags,
          ...remainingParams
        } = additionalParams || {};

        const appendedKeys = new Set();
        const appendIfDefined = (key, value) => {
          if (value !== undefined && value !== null && value !== '') {
            formData.append(key, value);
            appendedKeys.add(key);
          }
        };

        appendIfDefined('document_category_id', document_category_id);
        appendIfDefined('document_type_id', document_type_id);
        appendIfDefined('title', documentTitle || title);
        appendIfDefined(
          'loan_application_id',
          loanApplicationId ?? applicationId
        );
        appendIfDefined('borrower_id', borrowerId ?? userId);

        if (Array.isArray(tags) && tags.length > 0) {
          appendIfDefined('tags', tags.join(','));
        }

        Object.entries(remainingParams).forEach(([key, value]) => {
          if (value !== undefined && value !== null && !appendedKeys.has(key)) {
            formData.append(key, value);
          }
        });

        const headers = getHeaders();
        delete headers['Content-Type'];
        delete headers['content-type'];
        return {
          url: `documents/upload`,
          method: 'POST',
          headers,
          body: formData,
        };
      },
      invalidatesTags: ['updateList'],
    }),
    uploadB2cDocument: builder.mutation({
      query: ({ data, additionalParams }) => {
        const formData = new FormData();
        formData.append('file', data);
        const {
          document_category_id,
          document_type_id,
          documentTypeCode,
          documentTitle,
          title,
          applicationId,
          borrowerId,
          tags,
          ...remainingParams
        } = additionalParams || {};

        const appendedKeys = new Set();
        const appendIfDefined = (key, value) => {
          if (value !== undefined && value !== null && value !== '') {
            formData.append(key, value);
            appendedKeys.add(key);
          }
        };

        appendIfDefined('document_category_id', document_category_id);
        appendIfDefined('document_type_id', document_type_id);
        appendIfDefined('document_type_code', documentTypeCode);
        appendIfDefined('title', documentTitle || title);
        appendIfDefined('applicationId', applicationId);
        appendIfDefined('borrowerId', borrowerId);

        if (Array.isArray(tags) && tags.length > 0) {
          appendIfDefined('tags', tags.join(','));
        }

        Object.entries(remainingParams).forEach(([key, value]) => {
          if (value !== undefined && value !== null && !appendedKeys.has(key)) {
            formData.append(key, value);
          }
        });

        const headers = getHeaders();
        delete headers['Content-Type'];
        delete headers['content-type'];
        return {
          url: `b2c/loan/documents/upload`,
          method: 'POST',
          headers,
          body: formData,
        };
      },
      invalidatesTags: ['updateList'],
    }),
    uploadAndClassifyB2c: builder.mutation({
      query: ({ data, additionalParams }) => {
        const formData = new FormData();
        formData.append('file', data);
        if (additionalParams?.loan_application_id)
          formData.append('loan_application_id', additionalParams.loan_application_id);
        if (additionalParams?.borrower_id)
          formData.append('borrower_id', additionalParams.borrower_id);
        if (additionalParams?.title)
          formData.append('title', additionalParams.title);
        const headers = getHeaders();
        delete headers['Content-Type'];
        delete headers['content-type'];
        return {
          url: 'b2c/documents/upload-and-classify',
          method: 'POST',
          headers,
          body: formData,
        };
      },
      invalidatesTags: ['updateList'],
    }),
    updateB2cDocumentFile: builder.mutation({
      query: ({ data, additionalParams, documentId }) => {
        const formData = new FormData();
        formData.append('file', data);
        const { versionNotes, ...remainingParams } = additionalParams || {};
        if (versionNotes) {
          formData.append('version_notes', versionNotes);
        }
        Object.entries(remainingParams).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            formData.append(key, value);
          }
        });
        const headers = getHeaders();
        delete headers['Content-Type'];
        delete headers['content-type'];
        return {
          url: `b2c/documents/update_document_file/${documentId}`,
          method: 'POST',
          headers,
          body: formData,
        };
      },
      invalidatesTags: ['updateList'],
    }),
    getDocumentSummaries: builder.query({
      query: ({ documentIds }) => ({
        url: 'b2b/documents/summaries',
        method: 'GET',
        params: { document_ids: documentIds.join(',') },
        headers: getHeaders(),
      }),
      // The AI summary lands in Mongo at the END of processing, while the id
      // set stays the same — without a tag the cached "not yet complete"
      // result would never be replaced.
      providesTags: ['documentInsights'],
      transformResponse: (response) => response?.data ?? {},
    }),
    getDocumentAccuracyMetrics: builder.query({
      query: ({ documentIds }) => ({
        url: 'b2b/documents/accuracy-metrics',
        method: 'GET',
        params: { document_ids: documentIds.join(',') },
        headers: getHeaders(),
      }),
      // The AI summary lands in Mongo at the END of processing, while the id
      // set stays the same — without a tag the cached "not yet complete"
      // result would never be replaced.
      providesTags: ['documentInsights'],
      transformResponse: (response) => response?.data ?? {},
    }),
    updatePredefinedDocs: builder.mutation({
      query: ({ data, additionalParams, documentId }) => {
        const formData = new FormData();
        formData.append('file', data);
        const { versionNotes, ...remainingParams } = additionalParams || {};
        if (versionNotes) {
          formData.append('version_notes', versionNotes);
        }
        Object.entries(remainingParams).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            formData.append(key, value);
          }
        });
        const headers = getHeaders();
        delete headers['Content-Type'];
        delete headers['content-type'];
        return {
          url: `documents/${documentId}/versions`,
          method: 'POST',
          headers,
          body: formData,
        };
      },
      invalidatesTags: ['updateList'],
    }),
  }),
});

// api for view document is present in hooks > pdfViewer.js

export const {
  useGetDocumentListQuery,
  useGetB2cDocumentListQuery,
  useUploadPredefinedDocsMutation,
  useUploadB2cDocumentMutation,
  useUpdatePredefinedDocsMutation,
  useUpdateB2cDocumentFileMutation,
  useGetUserPropertyQuery,
  useDeleteDocumentMutation,
  useUploadAndClassifyB2cMutation,
  useDeleteB2cDocumentMutation,
  useGetDocumentSummariesQuery,
  useGetDocumentAccuracyMetricsQuery,
} = documentAPI;
